import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Flex,
    Box,
    Text,
    Input,
    Avatar,
    Spinner,
    IconButton,
    Divider,
} from "@chakra-ui/react";
import { SendIcon } from "../../definitions/constants/IconsComponent";
import { colors } from "../../definitions/constants/GlobalStyles";
import i18n from "../../i18n/i18n";
import { consumeApi } from "../../utils/tools";
import { appStore, postStore } from "../../zustand/globalStore";
import { CommentsData } from "../../definitions/interfaces/GlobalInterfaces";

interface CommentsModalProps {
    post?: any;
}

const CommentsModalComponent: React.FC<CommentsModalProps> = () => {
    const { showCommentsModal, setShowCommentsModal, selectedPost, setSelectedPost } = postStore((state) => state);
    const { userData } = appStore((state) => state);

    const [comments, setComments] = useState<any[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [mentionedUser, setMentionedUser] = useState<any>(null);

    const isOpen = showCommentsModal && selectedPost != null;
    const onClose = () => {
        setShowCommentsModal(false);
        setSelectedPost(null);
    };

    const fetchComments = async () => {
        setLoadingComments(true);
        try {
            const response = await consumeApi(
                "posts/get_comments_for_post",
                "POST",
                {
                    post_id: selectedPost?.id,
                }
            );
            if (response?.status === "success") {
                setComments(response.comments || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingComments(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchComments();
        }
    }, [isOpen]);

    const handleCreateComment = async () => {
        if (isSaving || !commentText.trim() || !userData) return;
        setIsSaving(true);

        let structure_text = null;
        if (mentionedUser && commentText.includes(`@${mentionedUser.firstname} ${mentionedUser.lastname}`)) {
            structure_text = {
                mention_1: {
                    id: mentionedUser.id,
                    type: "user",
                    object: {
                        firstname: mentionedUser.firstname,
                        id: mentionedUser.id,
                        image_url: mentionedUser.image_url,
                        lastname: mentionedUser.lastname,
                        username: mentionedUser.username || mentionedUser.firstname,
                    }
                }
            };
        }

        try {
            const response = await consumeApi("posts/create_comment", "POST", {
                comment_text: commentText,
                user_id: userData?.id,
                post_id: selectedPost?.id,
                structure_text,
            });

            if (response?.status === "success") {
                setCommentText("");
                setMentionedUser(null);
                fetchComments();
                // Optimistically update comments count? 
                if (selectedPost.comments) {
                    selectedPost.comments.push(response.created_comment);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCommentClick = (comment: any) => {
        setMentionedUser(comment);
        setCommentText(`@${comment.firstname} ${comment.lastname} `);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="20px" height="80vh">
                <ModalHeader borderBottom={`1px solid ${colors.figmaColors.grey0}`}>
                    <Flex justify="center" align="center">
                        <Box width="40px" height="4px" bg={colors.backgrounds.gray2} borderRadius="10px" />
                    </Flex>
                    <Text textAlign="center" mt={4} fontSize="18px" fontWeight="600" color={colors.text.black}>
                        {i18n.t("comments")}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody>
                    {loadingComments ? (
                        <Flex justify="center" align="center" height="100%">
                            <Spinner color="#2196F3" />
                        </Flex>
                    ) : comments.length > 0 ? (
                        <Flex direction="column" gap={4}>
                            {comments.map((comment: any, index: number) => (
                                <Flex key={comment.id || index} gap={3} onClick={() => handleCommentClick(comment)} cursor="pointer">
                                    <Avatar
                                        boxSize="40px"
                                        src={comment.image_url}
                                        name={`${comment.firstname} ${comment.lastname}`}
                                    />
                                    <Flex direction="column" flex="1">
                                        <Flex justify="space-between" align="center">
                                            <Flex align="center" gap={2}>
                                                <Text fontSize="13px" fontWeight="600" color={colors.figmaColors.black}>
                                                    {`${comment.firstname || ""} ${comment.lastname || ""}`}
                                                </Text>
                                                <Text fontSize="11px" color={colors.figmaColors.grey3}>
                                                    {(() => {
                                                        if (!comment.created_at) return '';
                                                        const createdAt = new Date(comment.created_at);
                                                        const now = new Date();
                                                        const diffMs = now.getTime() - createdAt.getTime();
                                                        const diffSeconds = Math.floor(diffMs / 1000);
                                                        const diffMinutes = Math.floor(diffSeconds / 60);
                                                        const diffHours = Math.floor(diffMinutes / 60);
                                                        const diffDays = Math.floor(diffHours / 24);

                                                        if (diffMinutes < 60) {
                                                            return `${diffMinutes} ${i18n.t("minutes")}`;
                                                        } else if (diffHours < 48) {
                                                            return `${diffHours} ${i18n.t("hours")}`;
                                                        } else if (diffDays < 30) {
                                                            return `${diffDays} ${i18n.t("days")}`;
                                                        } else {
                                                            return createdAt.toLocaleDateString();
                                                        }
                                                    })()}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                        <Text fontSize="13px" color={colors.figmaColors.grey3} mt={1}>
                                            {comment.text || comment.comment_text}
                                        </Text>
                                    </Flex>
                                </Flex>
                            ))}
                        </Flex>
                    ) : (
                        <Flex direction="column" justify="center" align="center" height="100%" gap={2}>
                            <Text fontSize="18px" fontWeight="600" color={colors.text.black}>{i18n.t("firstComment")}</Text>
                            <Text fontSize="13px" color={colors.text.lightGray3}>{i18n.t("sharedAndEnjoy")}</Text>
                        </Flex>
                    )}
                </ModalBody>

                <ModalFooter borderTop={`1px solid ${colors.figmaColors.grey0}`}>
                    <Flex
                        w="100%"
                        align="center"
                        bg={colors.backgrounds.gray0}
                        borderRadius="20px"
                        p={2}
                        px={4}
                    >
                        <Input
                            variant="unstyled"
                            placeholder={i18n.t("writeYourComment")}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleCreateComment();
                            }}
                            flex="1"
                        />
                        <IconButton
                            aria-label="Send comment"
                            icon={<SendIcon />}
                            variant="ghost"
                            isLoading={isSaving}
                            isDisabled={!commentText.trim() || isSaving}
                            onClick={handleCreateComment}
                            _hover={{ bg: "transparent" }}
                        />
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CommentsModalComponent;
